import { useState } from 'react';
import {
  MapPin, MessageCircle, Phone, Mail, Facebook, Instagram, Youtube, Linkedin,
  ExternalLink, CheckCircle2, X, ChevronLeft, ChevronRight, ChevronDown,
} from 'lucide-react';

// dev-builder-200 Fase 3: render de la landing (editor preview Y página
// pública). Modelo V2: UN `development` (objeto, no array) por landing, con
// `units` ya curadas por el admin (publish_on_landing) -- pero esta función
// SIEMPRE filtra por publish_on_landing de nuevo (no confía en que el caller
// ya haya filtrado): el preview del editor manda las 225 unidades crudas, la
// pública ya las manda filtradas -- ambos casos quedan correctos.

function formatPrice(value, currency = 'MXN') {
  if (value === null || value === undefined || value === '') return null;
  try {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
  } catch {
    return `$${value}`;
  }
}

function fontFormatFor(url) {
  const ext = (url || '').toLowerCase().split('.').pop();
  if (ext === 'otf') return 'opentype';
  if (ext === 'woff2') return 'woff2';
  if (ext === 'woff') return 'woff';
  return 'truetype';
}

const DEFAULT_STATUS_LABELS = { disponible: 'Disponible', apartado: 'Apartado', vendido: 'Vendido' };
const CUSTOM_FONT_FAMILY = 'DevBuilderCustomFont';

export default function DevLandingPublic({ landing, development, units = [], company = {} }) {
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  if (!landing) return <div className="p-8 text-center">Cargando landing...</div>;

  const tipografia = landing.tipografia || {};
  const bgColors = tipografia.section_background_colors || {};
  const sectionBg = (key) => bgColors[key] || undefined;
  const dev = development || {};

  const publishedUnits = units.filter((u) => u.publish_on_landing);
  const statusLabels = { ...DEFAULT_STATUS_LABELS, ...(landing.disponibilidad?.status_labels || {}) };
  const filteredUnits = selectedStatus ? publishedUnits.filter((u) => u.status === selectedStatus) : publishedUnits;

  const galleryImages = (landing.galeria?.images || []).filter((img) => img.url);

  // ============================================================================
  // FUENTE PERSONALIZADA (@font-face inyectado si la landing tiene una)
  // ============================================================================
  const customFont = tipografia.font_file_url ? (
    <style>{`
      @font-face {
        font-family: '${CUSTOM_FONT_FAMILY}';
        src: url('${tipografia.font_file_url}') format('${fontFormatFor(tipografia.font_file_url)}');
        font-display: swap;
      }
    `}</style>
  ) : null;
  const bodyFontFamily = tipografia.font_file_url
    ? `'${CUSTOM_FONT_FAMILY}', ${tipografia.primary_font || 'Inter'}, sans-serif`
    : `${tipografia.primary_font || 'Inter'}, sans-serif`;

  // ============================================================================
  // SECTION 1: HERO
  // ============================================================================
  const renderHero = () => {
    const hero = landing.hero || {};
    const isVideo = hero.media_type === 'video' && hero.media_url;
    return (
      <section
        className="relative min-h-[60vh] md:min-h-screen flex flex-col items-center justify-center text-center px-4 py-16 overflow-hidden"
        style={{
          backgroundImage: !isVideo && hero.media_url ? `url('${hero.media_url}')` : (!hero.media_url ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : undefined),
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          // Parallax: backgroundAttachment:fixed no funciona en iOS Safari (limitación
          // conocida de WebKit, no bug propio) -- ahí simplemente no-opea a scroll normal.
          backgroundAttachment: 'fixed',
          backgroundColor: tipografia.hero_bg_color || '#667eea',
        }}
      >
        {isVideo && (
          <video
            src={hero.media_url}
            autoPlay muted loop playsInline
            className="absolute inset-0 w-full h-full object-cover -z-0"
          />
        )}
        <div className="relative z-10 max-w-2xl mx-auto">
          <h1 className="text-5xl font-bold mb-4 drop-shadow-lg" style={{ color: tipografia.hero_title_color || '#ffffff' }}>{hero.title}</h1>
          <p className="text-xl mb-8 drop-shadow-lg" style={{ color: tipografia.hero_subtitle_color || '#ffffff' }}>{hero.subtitle}</p>
          <a
            href="#disponibilidad"
            className="inline-block px-8 py-3 font-semibold rounded-lg transition"
            style={{ backgroundColor: tipografia.accent_color || '#ffffff', color: '#ffffff' }}
          >
            {hero.cta_text || 'Ver proyectos'}
          </a>
        </div>
      </section>
    );
  };

  // ============================================================================
  // SECTION 2: PROYECTOS (un solo desarrollo)
  // ============================================================================
  const renderProyectos = () => {
    const sect = landing.proyectos || {};
    if (!dev.id) return null;
    return (
      <section className="py-16 px-4" style={{ backgroundColor: sectionBg('proyectos') || '#ffffff' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-2 text-center" style={{ color: tipografia.section_title_color || undefined }}>{sect.title}</h2>
          {sect.description && (
            <p className="text-center mb-12" style={{ color: tipografia.section_subtitle_color || undefined }}>{sect.description}</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              {sect.show_logo && (sect.logo_url || dev.logo_url) && (
                <img src={sect.logo_url || dev.logo_url} alt={dev.name} className="h-16 object-contain mb-4" />
              )}
              {sect.show_development_images && dev.cover_image_url && (
                <img src={dev.cover_image_url} alt={dev.name} className="w-full h-64 object-cover rounded-lg mb-4" />
              )}
              <h3 className="text-2xl font-semibold mb-2">{dev.name}</h3>
              {dev.description && <p className="text-gray-600 mb-3">{dev.description}</p>}
              {sect.show_price_range && dev.base_price != null && (
                <p className="font-medium mb-2">Desde {formatPrice(dev.base_price, dev.currency)}</p>
              )}
            </div>
            {sect.show_map && (dev.address || dev.city || dev.google_maps_url) && (
              <div className="bg-gray-100 rounded-lg p-6 flex flex-col items-center justify-center text-center h-64">
                <MapPin className="h-10 w-10 text-blue-600 mb-2" />
                <p className="font-medium">{[dev.address, dev.city].filter(Boolean).join(', ')}</p>
                {dev.google_maps_url && (
                  <a href={dev.google_maps_url} target="_blank" rel="noreferrer" className="mt-3 text-blue-600 flex items-center gap-1 text-sm font-medium">
                    Ver en Google Maps <ExternalLink className="h-4 w-4" />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  // ============================================================================
  // SECTION 3: DISPONIBILIDAD (unidades curadas, no todas)
  // ============================================================================
  const renderDisponibilidad = () => {
    const sect = landing.disponibilidad || {};
    if (publishedUnits.length === 0) return null;
    const uniqueStatuses = [...new Set(publishedUnits.map((u) => u.status).filter(Boolean))];

    return (
      <section id="disponibilidad" className="py-16 px-4" style={{ backgroundColor: sectionBg('disponibilidad') || '#f9fafb' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-2 text-center" style={{ color: tipografia.section_title_color || undefined }}>{sect.title}</h2>
          {sect.description && (
            <p className="text-center mb-8" style={{ color: tipografia.section_subtitle_color || undefined }}>{sect.description}</p>
          )}

          {sect.show_status_filter && uniqueStatuses.length > 1 && (
            <div className="mb-8 flex flex-wrap gap-2 justify-center">
              <button
                onClick={() => setSelectedStatus(null)}
                className={`px-4 py-2 rounded-lg font-medium transition ${selectedStatus === null ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              >
                Todos ({publishedUnits.length})
              </button>
              {uniqueStatuses.map((status) => {
                const count = publishedUnits.filter((u) => u.status === status).length;
                return (
                  <button
                    key={status}
                    onClick={() => setSelectedStatus(status)}
                    className={`px-4 py-2 rounded-lg font-medium transition ${selectedStatus === status ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                  >
                    {statusLabels[status] || status} ({count})
                  </button>
                );
              })}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUnits.map((unit) => (
              <div key={unit.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {unit.landing_image && (
                  <img src={unit.landing_image} alt={unit.label} className="w-full h-40 object-cover" />
                )}
                <div className="p-4">
                  <h4 className="font-semibold text-gray-900">{unit.label || unit.type}</h4>
                  <p className="text-sm text-gray-600 mb-3">{unit.type}</p>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div><p className="text-gray-500">Precio</p><p className="font-semibold">{formatPrice(unit.price) || '-'}</p></div>
                    <div><p className="text-gray-500">m²</p><p className="font-semibold">{unit.area_m2 ?? '-'}</p></div>
                    <div><p className="text-gray-500">Recámaras</p><p className="font-semibold">{unit.bedrooms ?? '-'}</p></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // ============================================================================
  // SECTION 4: CARACTERÍSTICAS
  // ============================================================================
  const renderCaracteristicas = () => {
    const sect = landing.caracteristicas || {};
    // Amenidades de las UNIDADES publicadas (no developments.amenities): el dato
    // real vive por-unidad, ver dev-units-amenities-landing. Unificado sin
    // duplicados con un Set.
    const amenities = [...new Set(publishedUnits.flatMap((u) => u.amenities || []))];
    if (!sect.show_development_amenities || amenities.length === 0) return null;
    return (
      <section className="py-16 px-4" style={{ backgroundColor: sectionBg('caracteristicas') || '#ffffff' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold mb-2 text-center" style={{ color: tipografia.section_title_color || undefined }}>{sect.title}</h2>
          {sect.description && (
            <p className="text-center mb-8" style={{ color: tipografia.section_subtitle_color || undefined }}>{sect.description}</p>
          )}
          <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-1">
            {amenities.map((a) => (
              <div key={a} className="px-3 py-2 rounded hover:bg-gray-100 transition capitalize flex items-center gap-2" style={{ color: tipografia.text_color || undefined }}>
                {sect.show_icons && <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: tipografia.accent_color || '#0066cc' }} />}
                {a.replace(/_/g, ' ')}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // ============================================================================
  // SECTION 5: GALERÍA (con lightbox)
  // ============================================================================
  const renderGaleria = () => {
    const sect = landing.galeria || {};
    if (galleryImages.length === 0) return null;
    return (
      <section className="py-16 px-4" style={{ backgroundColor: sectionBg('galeria') || '#ffffff' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-2 text-center" style={{ color: tipografia.section_title_color || undefined }}>{sect.title}</h2>
          {sect.description && (
            <p className="text-center mb-12" style={{ color: tipografia.section_subtitle_color || undefined }}>{sect.description}</p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {galleryImages.map((img, idx) => (
              <button key={img.url + idx} onClick={() => setLightboxIndex(idx)} className="block group">
                <img src={img.url} alt={img.caption || ''} className="w-full h-32 object-cover rounded-lg group-hover:opacity-80 transition" />
                {img.caption && <p className="text-xs text-gray-500 mt-1 truncate">{img.caption}</p>}
              </button>
            ))}
          </div>
        </div>

        {lightboxIndex !== null && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setLightboxIndex(null)}>
            <button className="absolute top-4 right-4 text-white" onClick={() => setLightboxIndex(null)}>
              <X className="h-8 w-8" />
            </button>
            {lightboxIndex > 0 && (
              <button
                className="absolute left-4 text-white"
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }}
              >
                <ChevronLeft className="h-10 w-10" />
              </button>
            )}
            <div onClick={(e) => e.stopPropagation()} className="max-w-3xl max-h-[80vh] flex flex-col items-center">
              <img src={galleryImages[lightboxIndex].url} alt="" className="max-h-[70vh] object-contain" />
              {galleryImages[lightboxIndex].caption && (
                <p className="text-white mt-3 text-center">{galleryImages[lightboxIndex].caption}</p>
              )}
            </div>
            {lightboxIndex < galleryImages.length - 1 && (
              <button
                className="absolute right-4 text-white"
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }}
              >
                <ChevronRight className="h-10 w-10" />
              </button>
            )}
          </div>
        )}
      </section>
    );
  };

  // ============================================================================
  // SECTION 6: FAQ (accordion)
  // ============================================================================
  const renderFaq = () => {
    const sect = landing.faq || {};
    const items = sect.items || [];
    if (items.length === 0) return null;
    return (
      <section className="py-16 px-4" style={{ backgroundColor: sectionBg('faq') || '#f9fafb' }}>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: tipografia.section_title_color || undefined }}>{sect.title}</h2>
          <div className="space-y-3">
            {items.map((item, idx) => {
              const isOpen = openFaqIndex === idx;
              return (
                <div key={idx} className="bg-white rounded-lg border overflow-hidden">
                  <button
                    onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                    className="w-full px-4 py-3 flex justify-between items-center gap-3 hover:bg-gray-50 transition text-left"
                  >
                    <span className="font-semibold text-gray-900">{item.question}</span>
                    <ChevronDown className={`h-5 w-5 flex-shrink-0 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="px-4 py-3 bg-gray-50 text-gray-700 text-sm border-t">{item.answer}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  };

  // ============================================================================
  // SECTION 7: CONTACTO
  // ============================================================================
  const renderContacto = () => {
    const sect = landing.contacto || {};
    const hasContact = sect.phone || sect.email || sect.whatsapp;
    if (!hasContact) return null;
    return (
      <section className="py-16 px-4" style={{ backgroundColor: sectionBg('contacto') || '#eff6ff' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 text-center" style={{ color: tipografia.section_title_color || undefined }}>{sect.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {sect.phone && (
              <div className="bg-white p-6 rounded-lg text-center">
                <Phone className="h-8 w-8 mx-auto mb-3" style={{ color: tipografia.accent_color || '#0066cc' }} />
                <p className="text-gray-600 text-sm mb-2">Teléfono</p>
                <a href={`tel:${sect.phone}`} className="font-semibold text-gray-900" style={{ color: tipografia.link_color || undefined }}>{sect.phone}</a>
              </div>
            )}
            {sect.email && (
              <div className="bg-white p-6 rounded-lg text-center">
                <Mail className="h-8 w-8 mx-auto mb-3" style={{ color: tipografia.accent_color || '#0066cc' }} />
                <p className="text-gray-600 text-sm mb-2">Email</p>
                <a href={`mailto:${sect.email}`} className="font-semibold text-gray-900" style={{ color: tipografia.link_color || undefined }}>{sect.email}</a>
              </div>
            )}
            {sect.whatsapp && (
              <div className="bg-white p-6 rounded-lg text-center">
                <MessageCircle className="h-8 w-8 mx-auto mb-3" style={{ color: tipografia.accent_color || '#0066cc' }} />
                <p className="text-gray-600 text-sm mb-2">WhatsApp</p>
                <a href={`https://wa.me/${sect.whatsapp.replace(/\D/g, '')}`} className="font-semibold text-gray-900" style={{ color: tipografia.link_color || undefined }}>{sect.whatsapp}</a>
              </div>
            )}
          </div>

          {sect.show_contact_form && (
            <div className="bg-white p-8 rounded-lg max-w-xl mx-auto">
              <h3 className="text-xl font-semibold mb-4">Envía tu consulta</h3>
              <form className="space-y-3">
                <input type="text" placeholder="Nombre" className="w-full px-4 py-2 border rounded-lg" />
                <input type="email" placeholder="Email" className="w-full px-4 py-2 border rounded-lg" />
                <textarea placeholder="Mensaje" rows="3" className="w-full px-4 py-2 border rounded-lg"></textarea>
                <button
                  type="submit"
                  className="w-full px-4 py-2 text-white rounded-lg font-semibold"
                  style={{ backgroundColor: tipografia.accent_color || '#0066cc' }}
                >
                  Enviar
                </button>
              </form>
            </div>
          )}
        </div>
      </section>
    );
  };

  // ============================================================================
  // SECTION 8: REDES SOCIALES
  // ============================================================================
  const renderRedes = () => {
    const sect = landing.redes || {};
    const socials = [
      { key: 'facebook', icon: Facebook }, { key: 'instagram', icon: Instagram },
      { key: 'youtube', icon: Youtube }, { key: 'linkedin', icon: Linkedin },
    ];
    const active = socials.filter((s) => sect[s.key]);
    if (active.length === 0) return null;
    return (
      <section className="py-12 px-4" style={{ backgroundColor: sectionBg('redes') || '#ffffff' }}>
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex justify-center gap-6">
            {active.map(({ key, icon: Icon }) => (
              <a key={key} href={sect[key]} target="_blank" rel="noreferrer" className="p-3 bg-gray-100 rounded-full hover:opacity-80 transition" style={{ color: tipografia.accent_color || undefined }}>
                <Icon className="h-6 w-6" />
              </a>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // ============================================================================
  // FOOTER
  // ============================================================================
  const renderFooter = () => {
    const contacto = landing.contacto || {};
    const footer = landing.footer || {};
    return (
      <footer className="py-8 px-4 text-white text-center" style={{ backgroundColor: sectionBg('footer') || '#111827' }}>
        <div className="max-w-6xl mx-auto">
          {footer.logo_url && <img src={footer.logo_url} alt="" className="h-10 object-contain mx-auto mb-3" />}
          <p className="text-sm mb-2">{company.name}</p>
          {contacto.phone && <p className="text-xs text-gray-400">{contacto.phone}</p>}
          {contacto.email && <p className="text-xs text-gray-400">{contacto.email}</p>}
          <p className="text-xs text-gray-500 mt-4">
            © {new Date().getFullYear()} {company.name}. Todos los derechos reservados.
            {footer.privacy_url && (
              <> · <a href={footer.privacy_url} target="_blank" rel="noreferrer" className="underline">Aviso de privacidad</a></>
            )}
          </p>
        </div>
      </footer>
    );
  };

  return (
    <div className="w-full min-h-screen" style={{ color: tipografia.text_color || undefined, fontFamily: bodyFontFamily }}>
      {customFont}
      {renderHero()}
      {renderProyectos()}
      {renderDisponibilidad()}
      {renderCaracteristicas()}
      {renderGaleria()}
      {renderFaq()}
      {renderContacto()}
      {renderRedes()}
      {renderFooter()}
    </div>
  );
}
